import React, { FC, useEffect, useState } from 'react';
import Svg from 'svg-inline-react';
import { useOpeningForConnectButtonQuery, useSaveOpeningMutation, useSessionQuery } from '@src/graphql/generated';
import { Appearance, Button } from '@src/components/Button';
import { Avatar } from '@src/components/Avatar';
import { Image } from '@src/components/Image';
import { setErrorFlashMessage } from '@src/redux/actions/flashMessages';
import { useAppDispatch } from '@src/redux';
import { Tooltip } from '@src/components/Tooltip';
import InfoSvg from '@src/svgs/fr-info-icon.svg';
import { getInviteMessage } from '@src/util/getInviteMessage';
import { EditPitchProps } from './props';
import './styles.scss';

const MIN_MESSAGE_LENGTH = 80;

export const EditPitchModal: FC<EditPitchProps> = ({ onClose, openingId }) => {
  const dispatch = useAppDispatch();

  const { data: openingData, loading: isOpeningLoading } = useOpeningForConnectButtonQuery({
    variables: {
      id: openingId,
    },
  });

  const [saveOpening, { loading: isSaveOpeningLoading }] = useSaveOpeningMutation({
    onCompleted: () => {
      onClose();
    },
    onError: () => {
      dispatch(setErrorFlashMessage('Could not save pitch message. Please try again.'));
    },
  });

  const [message, setMessage] = useState<string>('');

  const { data } = useSessionQuery();

  const recruiter = data?.currentRecruiter;
  const companyProfile = data?.company.profile;

  const defaultMessage =
    openingData?.opening.pitch || getInviteMessage(data?.company?.name, openingData?.opening.title || undefined);

  const onSave = () => {
    saveOpening({
      variables: {
        openingId: openingId,
        openingInput: {
          pitch: message,
        },
      },
    });
  };

  useEffect(() => {
    if (!isOpeningLoading && !!openingData) {
      setMessage(defaultMessage);
    }
  }, [isOpeningLoading, openingData]);

  useEffect(() => {
    setMessage(defaultMessage);
  }, [defaultMessage]);

  if (isOpeningLoading) {
    return <div className="source-edit-pitch-modal">Loading...</div>;
  }

  return (
    <div className="source-edit-pitch-modal">
      <div className="source-edit-pitch-modal__title">
        Edit your Pitch
        <Tooltip overlay="Use :name: tag to dynamically insert the candidate’s name into the message." placement="top">
          <Svg className="source-edit-pitch-modal__info-icon" src={InfoSvg} />
        </Tooltip>
      </div>

      <div className="source-edit-pitch-modal__body">
        <div className="source-edit-pitch-modal__avatar-wrapper">
          <Avatar
            avatarColor={recruiter?.avatarColor}
            className="source-edit-pitch-modal__avatar"
            initials={recruiter?.nameInitials}
            size="x-large"
            source={recruiter?.avatarUrl}
          />

          {companyProfile?.logo?.fullUrl && (
            <Image className="source-edit-pitch-modal__logo" src={companyProfile?.logo?.fullUrl} />
          )}
        </div>

        <div className="source-edit-pitch-modal__message-wrapper">
          <div className="source-edit-pitch-modal__message-title">
            {recruiter?.firstName}
            {companyProfile?.name && ` - ${companyProfile?.name}`}
          </div>

          <textarea
            className="source-edit-pitch-modal__textarea"
            onChange={event => setMessage(event.target.value)}
            value={message}
          />

          <p className="source-edit-pitch-modal__message-min-limit">
            Your message needs to be at least {MIN_MESSAGE_LENGTH} characters
          </p>
        </div>
      </div>

      <div className="source-edit-pitch-modal__footer">
        <Button appearance={Appearance.SECONDARY} className="source-edit-pitch-modal__close" onClick={onClose}>
          Cancel
        </Button>

        <Button
          className="source-edit-pitch-modal__confirm"
          isDisabled={isSaveOpeningLoading || message.trim().length < MIN_MESSAGE_LENGTH}
          onClick={onSave}
        >
          {isSaveOpeningLoading ? 'Saving...' : 'Save and close'}
        </Button>
      </div>
    </div>
  );
};
